<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('dms-users::page.title.dms_users') }}</h2>
            <div>
                <b-datatable
                    :key="datatableKey"
                    :columns="columns"
                    :endpoint="listRoute"
                    :hidden-columns="hiddenColumns"
                    :initial-filters="initialFilters"
                    item-key="id"
                    has-filter-selector
                    :initial-sort="initialSort"
                >
                    <template #subtitle>
                        <v-btn color="primary" small rounded @click="exportXls()"> Export Users </v-btn>
                    </template>
                    <template #buttons>
                        <div class="px-2">
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        v-if="can('api.v1.dms-users.store') && can('dms-users.create')"
                                        color="primary"
                                        icon
                                        v-bind="attrs"
                                        :href="createItemUrl()"
                                        v-on="on"
                                    >
                                        <v-icon>mdi-plus-circle</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('dms-users::button.create') }}</span>
                            </v-tooltip>
                        </div>
                    </template>
                    <template #filters="{ filters }">
                        <v-col cols="4" class="pb-0">
                            <b-filter-input
                                v-model="filters.filters.name"
                                :label="t('users::label.name')"
                            ></b-filter-input>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-input-roles
                                v-model="filters.filters.roles"
                                :label="t('users::label.role')"
                                class="status-filter"
                                :hidden-items="['Device']"
                                :clearable="filters.filters.roles && filters.filters.roles !== null"
                                @change="(val) => (filters.roles = val)"
                            ></b-input-roles>
                        </v-col>
                        <v-col cols="4" class="pb-0">
                            <b-filter-select
                                v-model="filters.filters.status"
                                :items="filtersOptions.status"
                                :label="t('users::label.status')"
                                :clearable="filters.filters.status !== null"
                            >
                            </b-filter-select>
                        </v-col>

                        <v-col cols="4">
                            <v-autocomplete
                                v-model="filters.filters.pharmacy_site"
                                :clearable="filters.filters.pharmacy_site !== null"
                                :items="pharmacySitesOptions"
                                :label="t('patients::form.label.pharmacy_site')"
                                dense
                                multiple
                                outlined
                                rounded
                            ></v-autocomplete>
                        </v-col>
                        <v-col cols="4">
                            <v-autocomplete
                                v-model="filters.filters.pharmacy_group"
                                :clearable="filters.filters.pharmacy_group !== null"
                                :items="pharmacyGroupOptions"
                                :label="t('dms-users::form.label.pharmacy_group')"
                                dense
                                multiple
                                outlined
                                rounded
                            ></v-autocomplete>
                        </v-col>
                    </template>

                    <template #[`item.actions`]="{ item }">
                        <div class="table-actions">
                            <v-tooltip v-if="can('dms-users.export-record-as-pdf')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="exportRecordAsPdf(item)"
                                    >
                                        <v-icon size="24">mdi-file-export-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.export_record_as_pdf') }}</span>
                            </v-tooltip>

                            <v-tooltip v-if="can('api.v1.dms-users.update')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$root.goTo(editItemUrl(item))"
                                    >
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('users::button.edit') }}</span>
                            </v-tooltip>
                            <v-tooltip v-if="can('dms-users.preview')" top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$root.goTo(previewItemUrl(item))"
                                    >
                                        <v-icon>mdi-eye</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('consultants::button.details') }}</span>
                            </v-tooltip>
                            <v-tooltip
                                v-if="can('auth.impersonate') && item.status === 'active'"
                                top
                                transition="v-fab-transition"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$root.goTo(impersonateUrl(item))"
                                    >
                                        <v-icon>mdi-account</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('users::button.impersonate') }}</span>
                            </v-tooltip>
                            <b-delete
                                v-if="can('api.v1.dms-users.destroy')"
                                :path="deleteItemUrl(item)"
                                :text="t('default::confirmation.deletion')"
                                :title="t('default::confirmation.general')"
                                icon="mdi-trash-can"
                                @deleted="triggerDeleted"
                            >
                            </b-delete>
                        </div>
                    </template>
                    <template #[`item.status`]="{ item }">
                        <div class="grid-status">
                            <span>
                                {{ item.status }}
                            </span>
                        </div>
                    </template>
                    <template #[`item.created_at`]="{ item }">
                        {{ moment(item.created_at).format('DD.MM.YYYY') }}
                    </template>
                </b-datatable>
            </div>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            datatableKey: 0,
            downloadingFile: false,
            hiddenColumns: ['id'],
            initialSort: 'name',
            initialFilters: {
                status: null,
            },
            pharmacySitesOptions: [],
            pharmacyGroupOptions: [],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('users::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 170,
                },
                {
                    text: t('users::grid.label.created_at'),
                    value: 'created_at',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('users::grid.label.role'),
                    value: 'role',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('users::grid.label.status'),
                    value: 'status',
                    sortable: true,
                    width: 140,
                },

                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 200,
                    align: 'end',
                },
            ],
            filtersOptions: {
                status: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('users::options.status.active'),
                        value: 'active',
                    },
                    {
                        text: t('users::options.status.blocked'),
                        value: 'blocked',
                    },
                    {
                        text: t('users::options.status.suspended'),
                        value: 'suspended',
                    },
                ],
            },
        };
    },
    created() {
        this.loadPharmacySitesOptions();
        this.loadPharmacyGroupOptions();
    },
    methods: {
        exportXls() {
            const link = document.createElement('a');
            link.href = route('dms-users.export', { format: 'xls' });
            document.body.appendChild(link);
            link.click();
        },
        loadPharmacyGroupOptions() {
            axios.get(route('api.v1.pharmacy-groups.options', { sort: 'name' })).then((response) => {
                if (!response) {
                    return;
                }
                this.pharmacyGroupOptions = response.data.data.map((x) => ({ value: x.id, text: x.name }));
            });
        },
        loadPharmacySitesOptions() {
            axios.get(route('api.v1.pharmacy-sites.options', { sort: 'name' })).then((response) => {
                if (!response) {
                    return;
                }
                this.pharmacySitesOptions = response.data.data.map((x) => ({ value: x.id, text: x.name }));
            });
        },
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('dms-users.export-record-as-pdf', { dms_user: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        itemUrl(item) {
            return route('dms-users.get', { dms_user: item.id });
        },
        editItemUrl(item) {
            return route('dms-users.get', { dms_user: item.id });
        },
        previewItemUrl(item) {
            return route('dms-users.preview', { dms_user: item.id });
        },
        createItemUrl() {
            return route('dms-users.create');
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
        deleteItemUrl(item) {
            return route('api.v1.dms-users.destroy', { dms_user: item.id });
        },
        itemStatusBool(item) {
            return item.status === 'active' ? true : false;
        },
        updateStatus(item) {
            axios({
                method: 'patch',
                url: route('api.v1.dms-users.update', { dms_user: item.id }),
                data: { status: item.status === 'active' ? 'blocked' : 'active' },
            })
                .then((response) => {
                    item.status = response.data.data.status;
                    this.$root.notify('info', t('default::notification.saved'));
                })
                .catch((error) => {
                    if (error.response && error.response.status === 422) {
                        this.$store.dispatch('dms_users/setErrorMessages', error.response.data.errors);
                    } else {
                        this.$root.notify('error', error.response.data.error);
                    }
                });
        },
        impersonateUrl(item) {
            return route('auth.impersonate', { id: item.id });
        },
    },
};
</script>

<style></style>
